import { BehaviorSubject, Subject } from 'rxjs';
import { SupportService } from '../../support/interfaces/support';
import { User } from '../interfaces/user';
import { GrowthBookService } from './growth-book.service';
import { LocalStorageService } from './local-storage.service';
import { TrackingService } from './tracking.service';

export let _userServiceInstance: UserService;

export class UserService {
  static deps = [LocalStorageService, TrackingService, GrowthBookService, SupportService];

  onUserUpdate$ = new Subject<void>();

  _userPermissions = new BehaviorSubject<Map<string, boolean>>(new Map<string, boolean>());
  _userRbacRoles = new BehaviorSubject<Array<string>>([]);
  constructor(
    private _localStorageService: LocalStorageService,
    private _trackingService: TrackingService,
    private _growthBookService: GrowthBookService,
    private _supportService: SupportService
  ) {
    _userServiceInstance = this;
  }

  clearUser() {
    this._localStorageService.remove('userData');
    this._supportService.reset();
    this._userPermissions.next(new Map());
    this._trackingService.reset();
  }

  setUser(data: any) {
    this._localStorageService.set('userData', JSON.stringify(data));
    this.setUserPermissions(data?.rbac?.permissions);
    this.setUserRbacRoles(data?.rbac?.roles);
    this.onUserUpdate$.next();
  }

  setUserPermissions(permissions: any) {
    const permissionsMap = new Map<string, boolean>();
    if (permissions) {
      permissions.forEach(permission => {
        permissionsMap.set(permission.name, true);
      });
    }
    this._userPermissions.next(permissionsMap);
  }

  setUserRbacRoles(roles) {
    let rbacRoles = [];
    if (roles) {
      rbacRoles = roles.map(role => role.name);
    }
    this._userRbacRoles.next(rbacRoles);
  }

  getUser(): User {
    let userData: any;
    try {
      userData = JSON.parse(this._localStorageService.get('userData'));
    } catch (e) {
      return undefined;
    }

    if (!userData) {
      return undefined;
    }

    let profileImage = userData.image_url;
    profileImage = !profileImage || profileImage === 'undefined' ? undefined : profileImage;

    return {
      name: userData.display_name,
      email: userData.email,
      profileImage: profileImage,
      isOwner: userData.is_owner,
      id: userData.id,
      isHidden: userData.is_hidden,
      clusterId: userData.cluster_id,
      tfaEnabled: userData.is_tfa_enabled,
      createdTs: userData.created_ts,
      teamId: userData.team_id,
      emailVerified: userData.email_verified,
      isEmailVerificationBlock: userData.email_verification_block,
      teamName: userData.team_name,
      role: userData.additional_details?.role
    };
  }

  updateThirdPartyData(data: any) {
    const user = this.getUser();
    this._supportService.setCurrentUser(user);
    this._trackingService.setUser(user);
    this._trackingService.refresh();
    this._trackingService.updateIdentity(user);
    this._growthBookService.setId(user.email);
    this.updateCoralogixRumContext(user);
  }

  updateCoralogixRumContext(user) {
    if ((window as any).CoralogixRum) {
      (window as any).CoralogixRum.setUserContext({
        user_id: user.id.toString(),
        user_email: user.email
      });
    }
  }

  addWorkspaceToMemberships(workspace: { workspace_id: string; workspace_team_id: number; name: string }) {
    let userData: any;
    try {
      userData = JSON.parse(this._localStorageService.get('userData'));
    } catch (e) {
      return;
    }
    if (!userData) return;
    if (!Array.isArray(userData.team_memberships)) {
      userData.team_memberships = [];
    }
    const alreadyExists = userData.team_memberships.some(
      (m: any) => m.workspace_id === workspace.workspace_id
    );
    if (!alreadyExists) {
      userData.team_memberships.push({
        team_id: workspace.workspace_team_id,
        team_name: workspace.name,
        team_type: 'CLIENT',
        workspace_id: workspace.workspace_id
      });
    }
    this._localStorageService.set('userData', JSON.stringify(userData));
    this.onUserUpdate$.next();
  }

  removeWorkspaceFromMemberships(workspaceId: string) {
    let userData: any;
    try {
      userData = JSON.parse(this._localStorageService.get('userData'));
    } catch (e) {
      return;
    }
    if (!userData || !Array.isArray(userData.team_memberships)) return;
    userData.team_memberships = userData.team_memberships.filter(
      (m: any) => m.workspace_id !== workspaceId
    );
    this._localStorageService.set('userData', JSON.stringify(userData));
    this.onUserUpdate$.next();
  }

  updateTeamNameInStorage(newName: string) {
    let userData: any;
    try {
      userData = JSON.parse(this._localStorageService.get('userData'));
    } catch (e) {
      return;
    }
    if (!userData) return;

    // Update top-level team_name
    userData.team_name = newName;

    // Update current_team.team_name if team_id matches
    if (userData.current_team?.team_id === userData.team_id) {
      userData.current_team = { ...userData.current_team, team_name: newName };
    }

    // Update matching entry in team_memberships
    if (Array.isArray(userData.team_memberships)) {
      userData.team_memberships = userData.team_memberships.map((m: any) =>
        m.team_id === userData.team_id ? { ...m, team_name: newName } : m
      );
    }

    this._localStorageService.set('userData', JSON.stringify(userData));
    this.onUserUpdate$.next();
  }

  isISVTeam(): boolean {
    let userData: any;
    try {
      userData = JSON.parse(this._localStorageService.get('userData'));
    } catch (e) {
      return false;
    }
    return userData?.current_team?.team_type === 'ISV';
  }

  isWorkspaceTeam(): boolean {
    let userData: any;
    try {
      userData = JSON.parse(this._localStorageService.get('userData'));
    } catch (e) {
      return false;
    }
    return userData?.current_team?.team_type === 'WORKSPACE';
  }

  getWorkspaceContext(): { currentTeam: any; workspaceAccess: any[] } {
    let userData: any;
    try {
      userData = JSON.parse(this._localStorageService.get('userData'));
    } catch (e) {
      return { currentTeam: null, workspaceAccess: [] };
    }
    return {
      currentTeam: userData?.current_team ?? null,
      workspaceAccess: userData?.team_memberships ?? []
    };
  }

  public getPermissions() {
    return this._userPermissions.getValue();
  }

  public getUserRbacRoles() {
    return this._userRbacRoles.getValue();
  }
}
